<template>
  <div ref="checkoutContainer" class="container">
    <checkout-processing :processing="processing"></checkout-processing>
    <div class="row px-4">
      <div id="checkout-panel" ref="mainCol" class="col-md-8">
        <checkout-shipping-address></checkout-shipping-address>
        <hr />
        <checkout-billing-address></checkout-billing-address>
        <hr />
        <checkout-payment-method></checkout-payment-method>
        <hr />
        <checkout-items></checkout-items>
      </div>
      <div class="col-md-4">
        <checkout-submit
          ref="submitComponent"
          :sticky-active="stickyActive"
          :sticky-y-pos="stickyYPos"
          :sticky-end="stickyEnd"
          :sticky-end-pos="stickyEndPos"
        ></checkout-submit>
      </div>
    </div>
    <div class="row">
      <checkout-notice></checkout-notice>
    </div>
  </div>
</template>

<script>
import store from "../../store/stores";
import CheckoutBillingAddress from "./components/CheckoutBillingAddress.vue";
import CheckoutSubmit from "./components/CheckoutSubmit.vue";
import CheckoutShippingAddress from "./components/CheckoutShippingAddress.vue";
import CheckoutPaymentMethod from "./components/CheckoutPaymentMethod.vue";
import CheckoutItems from "./components/CheckoutItems.vue";
import CheckoutNotice from "./components/CheckoutNotice.vue";
import CheckoutProcessing from "./components/CheckoutProcessing.vue";

const SUBMIT_COMPONENT_BORDERS = 2;
const LAYOUT_COL_MD_BREAKPOINT = 991;

export default {
  store,
  components: {
    CheckoutProcessing,
    CheckoutItems,
    CheckoutSubmit,
    CheckoutBillingAddress,
    CheckoutShippingAddress,
    CheckoutPaymentMethod,
    CheckoutNotice,
  },
  props: {
    user: {
      required: true,
      type: Object,
    },
    stripeKey: {
      required: true,
      type: String,
    },
    stripeApis: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      primaryColumnHeight: null,
      endBreakpoint: null,
      submitHeight: null,
      stickyActive: false,
      stickyEnd: false,
    };
  },
  computed: {
    navigationHeight() {
      const el = document.getElementById("app");
      const style = el.currentStyle || window.getComputedStyle(el);
      return parseInt(style.marginTop, 10);
    },
    pageHeaderHeight() {
      const el = document.getElementsByClassName("page-header")[0];
      return el.getBoundingClientRect().height;
    },
    stickyYPos() {
      return (
        this.navigationHeight + this.pageHeaderHeight + SUBMIT_COMPONENT_BORDERS
      );
    },
    stickyEndPos() {
      return this.primaryColumnHeight - this.submitHeight;
    },
    processing() {
      return this.$store.getters["checkout/processing"];
    },
  },
  created() {
    const testDrives = this.user.test_drive_subscribers;
    this.$store.commit("checkout/SET_LOCALE", this.user.locale);
    this.$store.commit(
      "checkout/SET_REGIONS",
      this.user.locale.regions.map((region) => {
        return {
          name: region.region_name,
          code: region.region_code,
        };
      })
    );
    this.$store.commit(
      "checkout/SET_ACCEPT_NET_TERMS",
      !!this.user.accept_net_terms
    );
    this.$store.commit("checkout/SET_STRIPE_KEY", this.stripeKey);
    this.$store.commit("checkout/SET_STRIPE_APIS", this.stripeApis);
    this.$store.commit("checkout/SET_USER_ID", this.user.id);
    this.$store.commit("checkout/SET_ADDRESSES", this.user.addresses);
    this.$store.commit("checkout/SET_PAYMENT_METHODS", this.user.cards);
    this.$store.commit("checkout/SET_ACH_ACCESS", this.user.ach_access);
    this.$store.commit(
      "checkout/SET_ACH_CREDIT_ACCESS",
      this.user.ach_credit_access
    );
    this.$store.commit(
      "checkout/SET_STRIPE_PAYMENT_INTENT_ACCESS",
      this.user.stripe_payment_intent_flag
    );
    this.$store.commit(
      "checkout/SET_CART_ITEMS",
      this.user.shopping_cart_items.map((shoppingCartItem) => {
        return {
          id: shoppingCartItem.id,
          item: shoppingCartItem.item,
          quantity: shoppingCartItem.quantity,
          terms: false,
        };
      })
    );
    this.$store.commit("checkout/SET_TESTDRIVES", testDrives);
    this.$store.dispatch("checkout/setUpgradeableTestdrive", testDrives);
    this.$store.dispatch(
      "checkout/setUpgradeableNoImplementationFee",
      this.user.shopping_cart_items
    );
  },
  mounted() {
    window.addEventListener("scroll", this.handleSticky);
    window.addEventListener("resize", this.handleSticky);

    const options = JSON.parse(window.localStorage.getItem("checkoutOptions"));

    if (options) {
      this.parseLocalState(options);
    }
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleSticky);
    window.removeEventListener("resize", this.handleSticky);
  },
  methods: {
    parseLocalState(options) {
      if (options.expiresAt < Date.now()) {
        window.localStorage.removeItem("checkoutOptions");
        return;
      }

      if (options.sId) {
        if (
          this.$store.getters["checkout/addresses"].some(
            (address) => address.id === options.sId
          )
        ) {
          this.$store.commit("checkout/SET_SHIPPING_ADDRESS_ID", options.sId);
        }
      }

      if (options.bId) {
        if (
          this.$store.getters["checkout/addresses"].some(
            (address) => address.id === options.bId
          )
        ) {
          this.$store.commit("checkout/SET_BILLING_ADDRESS_ID", options.bId);
        }
      }

      if (options.pId) {
        if (
          this.$store.getters["checkout/paymentMethods"].some(
            (paymentMethod) => paymentMethod.id === options.pId
          )
        ) {
          this.$store.commit("checkout/SET_PAYMENT_METHOD_ID", options.pId);
        }
      }
    },
    handleSticky() {
      if (window.innerWidth <= LAYOUT_COL_MD_BREAKPOINT) {
        this.stickyActive = false;
        this.stickyEnd = false;
        return;
      }

      this.primaryColumnHeight =
        this.$refs.mainCol.getBoundingClientRect().height;
      this.submitHeight = this.$refs.submitComponent.getHeight();

      this.endBreakpoint =
        this.navigationHeight +
        (this.primaryColumnHeight -
          this.submitHeight -
          SUBMIT_COMPONENT_BORDERS);

      if (window.scrollY > this.endBreakpoint) {
        this.stickyActive = false;
        this.stickyEnd = true;
      } else {
        this.stickyActive = window.scrollY > this.pageHeaderHeight;
        this.stickyEnd = false;
      }
    },
  },
};
</script>
